import Text from "common/Typography/Text.tsx";
import InputBox from "common/Inputs/InputBox.tsx";
import { copyCodeToClipboard, openMessage } from "utils/misc.tsx";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils.ts";
import React, { useContext } from "react";
import {
  getProductId,
  getPromoCodeRoundedDiscount,
} from "@shared/features/settings/subscription/utils.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import Link from "common/Typography/Link.tsx";
import BottomDrawer from "common/Drawers/BottomDrawer.tsx";
import Button from "common/Buttons/Button.tsx";
import styled from "styled-components";
import { getEnvironmentType } from "app/shared/utils.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface AppleOfferCodeModalProps {
  open: boolean;
  closeModal: () => void;
  onApplePayClick?: () => void;
  buttonLoading?: boolean;
}

const AppleOfferCodeModal = ({
  open,
  closeModal,
  onApplePayClick,
  buttonLoading,
}: AppleOfferCodeModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { plan, plans, duration, promoCodePrices } = useContext(
    BuySubscriptionContext
  )!;
  const getAppleOfferCode = () => {
    const roundedDiscountPercent = getPromoCodeRoundedDiscount(
      plans,
      plan,
      duration,
      promoCodePrices
    );
    if (roundedDiscountPercent) {
      return getProductId(
        getEnvironmentType(),
        plan?.subscription_type,
        duration,
        roundedDiscountPercent
      );
    }
  };

  const appleOfferCode = getAppleOfferCode();

  const content = (
    <>
      <Text>
        {t("apple_offer_code.instructions", {
          payNow: t("payment_status.pay_now"),
        })}
      </Text>
      <InputBox
        style={INPUT_BOX_STYLE}
        onClick={() => {
          copyCodeToClipboard(appleOfferCode || "")
            .then(() =>
              openMessage(getCopyMessage(t("apple_offer_code.name")), "info")
            )
            .catch(() => {
              openMessage(t("apple_offer_code.copy_failed"), "warning");
            });
        }}
      >
        <OfferCode>{appleOfferCode}</OfferCode>
        <StyledLink>{t("apple_offer_code.copy_code")}</StyledLink>
      </InputBox>
      <StyledText>{t("apple_offer_code.enter_exactly")}</StyledText>
    </>
  );

  const footer = (
    <StyledButton
      type="primary"
      onClick={onApplePayClick}
      loading={buttonLoading}
    >
      {t("payment_status.pay_now")}
    </StyledButton>
  );

  return (
    <BottomDrawer
      title={t("apple_offer_code.title")}
      open={open}
      onClose={closeModal}
      content={content}
      footer={footer}
    />
  );
};

export default AppleOfferCodeModal;

const INPUT_BOX_STYLE = { margin: "12px 0" };
const OfferCode = styled(Text).attrs({ type: "secondary" })`
  flex: 1;
`;
const StyledLink = styled(Link)`
  padding-left: 8px;
`;
const StyledText = styled(Text)`
  font-style: italic;
`;
const StyledButton = styled(Button)`
  width: 100%;
`;
